import { OverlayViewF } from '@react-google-maps/api'
import { rem } from 'polished'
import type { MouseEventHandler } from 'react'
import { memo, useCallback, useContext, useEffect, useRef } from 'react'
import styled from 'styled-components'

import PinHeart from './PinAccessory/PinHeart'
import ExplorePin from './Pins/ExplorePin'
import SelectedMapItemPin from './Pins/SelectedMapItemPin'
import type { BaseMapElementComponentProps } from '../utils'
import { pinOffSetter } from '../utils'

import Clickable from 'components/Common/Clickable'
import usePrevious from 'hooks/usePrevious'
import useToggle from 'hooks/useToggle'
import type { ExploreCategory } from 'tripPlanner/pages/trip/Explore/common'
import type { ExploreItem } from 'tripPlanner/types/common'
import { exploreItemSelectionId } from 'tripPlanner/utils/itemSelection'
import { TripMapContext } from 'tripPlanner/contexts/TripMapContext'

const StyledClickable = styled(Clickable)`
  position: absolute;
  left: 0;
  bottom: 0;
  transform: translateX(-50%);
`

const PinHeartContainer = styled.div`
  position: absolute;
  bottom: ${rem(20)};
  left: ${rem(15)};
  z-index: 9999;
`

interface Props extends BaseMapElementComponentProps {
  item: ExploreItem
  category: ExploreCategory
  saved: boolean
}

function ExploreMapMarker({
  active,
  item,
  position,
  zIndex,
  category,
  saved,
}: Props) {
  const { onSelectMapItem, onDeselectMapItem } = useContext(TripMapContext)

  const onClick: MouseEventHandler = useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      if (active) {
        onDeselectMapItem()
      } else {
        onSelectMapItem(exploreItemSelectionId(item.id))
      }
    },
    [active, onDeselectMapItem, onSelectMapItem, item.id],
  )

  const ref = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (ref.current && google?.maps) {
      google.maps.OverlayView.preventMapHitsFrom(ref.current)
    }
  }, [ref])

  const [hovered, , onHover, offHover] = useToggle(false)

  const prevSaved = usePrevious(saved, saved)

  useEffect(() => {
    // When the item is saved, the saved item card + pin logic will pan it into view if required.
    if (saved && !prevSaved) {
      onSelectMapItem(exploreItemSelectionId(item.id))
    }

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [saved])

  const overlayZIndex = active || hovered ? 1000 : zIndex

  return (
    <OverlayViewF
      zIndex={overlayZIndex}
      position={position}
      getPixelPositionOffset={pinOffSetter}
      mapPaneName="overlayMouseTarget"
    >
      <StyledClickable
        onClick={onClick}
        ref={ref}
        onMouseEnter={onHover}
        onMouseLeave={offHover}
      >
        {saved && !active && (
          <PinHeartContainer>
            <PinHeart />
          </PinHeartContainer>
        )}
        {active && <SelectedMapItemPin text={item.name} />}
        {!active && <ExplorePin category={category} saved={saved} />}
      </StyledClickable>
    </OverlayViewF>
  )
}

export default memo(ExploreMapMarker)
